import { z } from 'zod';
import { EngagementStageSchema } from './engagement.js';
/** What the backend may ask the Engagement Lead to do. Every task is read-only and advisory. */
export const AgentTaskSchema = z.enum(['SUMMARISE', 'PROPOSE_NEXT_STAGE', 'SUGGEST_TRIAGE']);
export const AgentRunStatusSchema = z.enum(['QUEUED', 'RUNNING', 'SUCCEEDED', 'FAILED']);
/**
 * The payload the backend sends to the AgentCore runtime.
 *
 * The identity here is for audit and prompt context only. The agent does not trust it for
 * authorisation — its tools re-check `orgId` against every item they read (§5).
 */
export const AgentInvocationSchema = z.strictObject({
    engagementId: z.uuid(),
    orgId: z.uuid(),
    task: AgentTaskSchema,
    requestedByUserId: z.uuid(),
});
/**
 * The Engagement Lead's structured result (§8). Validated before the backend stores or acts
 * on it — a model that returns something else fails the run rather than being interpreted.
 *
 * `proposedNextStage` is a *proposal*. The transition whitelist and the approval-record
 * check in backend domain code decide whether it happens; the agent never moves a stage.
 */
/**
 * The agent's confidence in a suggestion, 0 to 10.
 *
 * Its own assessment against a fixed rubric (see `confidenceBand`), not a measured probability
 * and not a severity. Shown beside every suggestion so a person can weigh it — and never used by
 * code to decide anything.
 */
export const ConfidenceSchema = z.number().int().min(0).max(10);
/** What a confidence score means, in words. The prompt gives the agent the same rubric. */
export function confidenceBand(score) {
    if (score >= 9)
        return { label: 'Strong evidence', tone: 'high' };
    if (score >= 7)
        return { label: 'Likely — verify', tone: 'high' };
    if (score >= 4)
        return { label: 'Uncertain', tone: 'mid' };
    return { label: 'Weak — do not rely on it', tone: 'low' };
}
/**
 * Statuses the agent may suggest. Never FIXED — a scan can show an issue is present, not that it
 * was fixed — and never UNVERIFIED, which is not a decision.
 */
export const SuggestibleStatusSchema = z.enum(['CONFIRMED', 'FALSE_POSITIVE', 'ACCEPTED_RISK']);
/** The agent's suggestion for one finding. A person decides; this only informs them. */
/**
 * What an analysis may cite: identifiers of facts the knowledge tools returned for that finding.
 * `CISA-KEV` and `EPSS` stand for those sources' facts about the finding's CVEs;
 * `GUARDIAN-RULE-{id}` for Guardian's rule assessment; `OSV:{id}` for an advisory's fixed version.
 */
export const CITATION = /^(CVE-\d{4}-\d{4,7}|CWE-\d{1,5}|A\d{2}:2021|CISA-KEV|EPSS|GUARDIAN-RULE-\d{1,6}|OSV:[A-Za-z0-9-]{1,60})$/;
/**
 * The agent's reading of one finding — AI-derived, never a source fact (ADR 0006). Every claim
 * rests on the cited facts; the worker drops an analysis that cites, or names, anything the
 * knowledge base does not hold for that finding.
 */
export const FindingAnalysisSchema = z.strictObject({
    summary: z.string().min(10).max(600),
    attackScenario: z.string().min(10).max(800),
    businessImpact: z.string().min(10).max(600),
    recommendedAction: z.string().min(10).max(800),
    citations: z.array(z.string().regex(CITATION)).min(1).max(20),
});
export const FindingSuggestionSchema = z.strictObject({
    findingId: z.uuid(),
    suggestedStatus: SuggestibleStatusSchema,
    /** Why, citing the evidence in the record. Drafted for a person to check, edit or reject. */
    reason: z.string().min(10).max(1000),
    confidence: ConfidenceSchema,
    /** Present when the knowledge base had facts to analyse; grounded or dropped by the worker. */
    analysis: FindingAnalysisSchema.optional(),
});
export const EngagementLeadResultSchema = z.strictObject({
    summary: z.string().min(1).max(4000),
    proposedNextStage: EngagementStageSchema.nullable(),
    /** Confidence in `proposedNextStage` — including in proposing no move at all. */
    confidence: ConfidenceSchema,
    rationale: z.string().min(1).max(4000),
    /** One per unverified finding, for SUGGEST_TRIAGE; empty for every other task. */
    findingSuggestions: z.array(FindingSuggestionSchema).max(200),
    risks: z.array(z.string().min(1).max(500)).max(20),
    /** Anything the agent could not settle from evidence. Surfaced to the approver verbatim. */
    questionsForHuman: z.array(z.string().min(1).max(500)).max(20),
    /** Version of the system prompt that produced this result, for auditability (§8). */
    promptVersion: z.string().min(1).max(40),
});
//# sourceMappingURL=agent-run.js.map